import React from 'react';
import RequiredApiNotice from './RequiredApiNotice';

interface TeamBuildingDashboardProps {
  data: any;
  address: any;
}

const TeamBuildingDashboard: React.FC<TeamBuildingDashboardProps> = ({ data }) => {

  /** Treat undefined, null, empty string, or literal "undefined" as missing for display */
  const cellValue = (v: any): string => {
    if (v == null || v === '') return '—';
    const s = String(v).trim();
    if (s === '' || s.toLowerCase() === 'undefined') return '—';
    return s;
  };

  const isFieldFilled = (value: any): boolean => {
    if (value == null || value === '') return false;
    const text = String(value).trim();
    return text !== '' && text !== '—' && text.toLowerCase() !== 'undefined';
  };

  /** Row has every column populated (name, phone, company, email, website). */
  const isRowFullyComplete = (row: any): boolean => {
    if (!row) return false;
    return (
      isFieldFilled(row.name) &&
      isFieldFilled(row.phone) &&
      isFieldFilled(row.company) &&
      isFieldFilled(row.email) &&
      isFieldFilled(row.companyUrl || row.website)
    );
  };

  /** Keep rows that have a name plus at least one contact field. */
  const hasContactInfo = (row: any): boolean => {
    if (!row) return false;
    return (
      isFieldFilled(row.phone) ||
      isFieldFilled(row.company) ||
      isFieldFilled(row.email) ||
      isFieldFilled(row.companyUrl || row.website)
    );
  };

  const sortPartnerRows = (rows: any[] | undefined) =>
    [...(rows || [])]
      .filter((row) => isFieldFilled(row?.name) && hasContactInfo(row))
      .sort((a, b) => {
        const aHasPhone = isFieldFilled(a.phone) ? 1 : 0;
        const bHasPhone = isFieldFilled(b.phone) ? 1 : 0;
        if (bHasPhone !== aHasPhone) return bHasPhone - aHasPhone;
        const aHasEmail = isFieldFilled(a.email) ? 1 : 0;
        const bHasEmail = isFieldFilled(b.email) ? 1 : 0;
        if (bHasEmail !== aHasEmail) return bHasEmail - aHasEmail;
        const aScore = isRowFullyComplete(a) ? 1 : 0;
        const bScore = isRowFullyComplete(b) ? 1 : 0;
        if (bScore !== aScore) return bScore - aScore;
        return String(a.name || '').localeCompare(String(b.name || ''));
      })
      .slice(0, 10);
  const realtorsRows = data?.realtors || [];
  const contractorsRows = data?.contractors || [];
  const propertyManagersRows = data?.propertyManagers || [];
  const lendersRows = data?.lenders || [];
  const hardMoneyRows = data?.hardMoneyLenders || [];
  const combinedLenderRows = [...lendersRows, ...hardMoneyRows];

  const missingFields: string[] = [];
  if (!realtorsRows.length) missingFields.push('Realtors');
  if (!contractorsRows.length) missingFields.push('Contractors');
  if (!propertyManagersRows.length) missingFields.push('Property Managers');
  if (!combinedLenderRows.length) missingFields.push('Lenders');

  const normalizeUrl = (raw: any) => {
    const val = cellValue(raw);
    if (val === '—') return val;
    if (/^https?:\/\//i.test(val)) return val;
    return `https://${val}`;
  };

  const renderPartnerTable = (title: string, rows: any[] | undefined) => {
    const completedRows = sortPartnerRows(rows);
    return (
      <div className="card" style={{ marginTop: '16px' }}>
        <h3 style={{ marginBottom: 10 }}>{title}</h3>
        <div style={{ overflowX: 'auto' }}>
          <table
            style={{
              width: '100%',
              borderCollapse: 'collapse',
              fontSize: 13,
            }}
          >
            <thead>
              <tr style={{ borderBottom: '1px solid #e5e7eb' }}>
                <th style={{ textAlign: 'left', padding: '8px 6px' }}>Name</th>
                <th style={{ textAlign: 'left', padding: '8px 6px' }}>Phone</th>
                <th style={{ textAlign: 'left', padding: '8px 6px' }}>Company</th>
                <th style={{ textAlign: 'left', padding: '8px 6px' }}>Email</th>
                <th style={{ textAlign: 'left', padding: '8px 6px' }}>Website</th>
              </tr>
            </thead>
            <tbody>
              {completedRows.length > 0 ? (
                completedRows.map((row, idx) => {
                  const url = normalizeUrl(row.companyUrl || row.website);
                  return (
                <tr key={cellValue(row.name) + cellValue(row.company) + idx} style={{ borderBottom: '1px solid #f3f4f6' }}>
                  <td style={{ padding: '6px 6px', whiteSpace: 'nowrap' }}>{cellValue(row.name)}</td>
                      <td style={{ padding: '6px 6px', whiteSpace: 'nowrap' }}>{cellValue(row.phone)}</td>
                      <td style={{ padding: '6px 6px' }}>{cellValue(row.company)}</td>
                      <td style={{ padding: '6px 6px' }}>{cellValue(row.email)}</td>
                      <td style={{ padding: '6px 6px' }}>
                        {url !== '—' ? (
                          <a href={url} target="_blank" rel="noreferrer" style={{ color: '#1565c0', textDecoration: 'none' }}>
                            {cellValue(row.companyUrl || row.website).replace(/^https?:\/\/(www\.)?/i, '').split('/')[0]}
                          </a>
                        ) : (
                          '—'
                        )}
                      </td>
                    </tr>
                  );
                })  
              ) : (
                <tr>
                  <td colSpan={5} style={{ padding: '8px 6px', textAlign: 'center', color: '#6b7280' }}>
                    Data not available.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  return (
    <div className="evaluation-section">
      <header className="evaluation-section-header">
        <div>
          <p className="dashboard-eyebrow">T • Team building</p>
          <h2>T - TEAM BUILDING (Investor Ecosystem)</h2>
          <p className="evaluation-section-subtitle">
            Can I realistically operate in this market remotely?
          </p>
        </div>
      </header>

      {renderPartnerTable('Realtors', realtorsRows)}
      {renderPartnerTable('Contractors', contractorsRows)}
      {renderPartnerTable('Property Managers', propertyManagersRows)}
      {renderPartnerTable('Investment Property Lenders', lendersRows)}
      {renderPartnerTable('Hard Money Lenders', hardMoneyRows)}

      {missingFields.length > 0 && (
        <RequiredApiNotice
          apis={['WTI Team Building CSV Database', 'Google Places Nearby Search API']}
          missingFields={missingFields}
        />
      )}
    </div>
  );
};

export default TeamBuildingDashboard;
